import { FC } from 'react';
import { Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import AuthWrapper from '../../common/AuthWrapper';
import CustomInput from '../../common/CustomInput';
import CustomPassInput from '../../common/CustomPassInput';
import CustomButton from '../../common/CustomButton';
import { useIsMobile } from '../../hooks/useWindowWidth';

import styles from './CreateAccount.module.scss';

const CreateAccount: FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  };

  const handleSubmit = (values: {
    firstName: string,
    lastName: string,
    email: string,
    password: string
  }) => {
    localStorage.setItem("createAccount", JSON.stringify(values));
    navigate('/personalized-form');
  };

  const formikValidate = (values: {
    firstName: string, lastName: string, email: string; password: string
  }) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[^\s]{8,}$/;

    const errors: {
      firstName?: string, lastName?: string, email?: string; password?: string
    } = {};
    if (!values.firstName && !errors.lastName) {
      errors.firstName = 'First name is required';
    }
    if (!values.lastName && errors.firstName) {
      errors.lastName = 'Last name is required';
    }
    if (!values.email && !errors.lastName && !errors.firstName) {
      errors.email = 'Email is required';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
    ) {
      errors.email = 'Invalid email address';
    }
    if (!values.password && !errors.email) {
      errors.password = 'Password can\'t be empty';
    }
    if (!values.password) {
      errors.password = 'Password can\'t be empty';
    } else if (values.password.length <= 8) {
      errors.password = 'Password length should be at least 8 characters';
    } else if (!passwordRegex.test(values.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
    return errors;
  }

  return (
    <AuthWrapper btnName='Sign Up'>
      <Box className={styles.loginContent}>
        <Typography variant={`${isMobile ? 'h6Bold' : 'h5Bold'}`} className={styles.title}>Create new account</Typography>
        <Formik
          initialValues={initialValues}
          validate={formikValidate}
          onSubmit={handleSubmit}
        >
          {({ values, errors, handleChange, isSubmitting }) => (
            <Form className={styles.loginForm}>
              
              <Box className={styles.nameInputs}>
                <CustomInput
                  inputLabel='First Name'
                  inputType='text'
                  inputName="firstName"
                  value={values.firstName}
                  error={errors.firstName}
                  customClass={styles.customInput}
                  onChange={handleChange}
                />
                <CustomInput
                  inputLabel='Last Name'
                  inputType='text'
                  inputName="lastName"
                  value={values.lastName}
                  error={errors.lastName}
                  customClass={styles.customInput}
                  onChange={handleChange}
                />
              </Box>
              <CustomInput
                inputType='email'
                inputName="email"
                value={values.email}
                error={errors.email}
                customClass={styles.customInput}
                onChange={handleChange}
              />
              <CustomPassInput
                label='Password'
                error={errors.password}
                customClass={styles.customInput}
                onChange={handleChange}
              />

              <CustomButton
                btnType='submit'
                customClass={styles.customButton}
                disabled={isSubmitting}
              >
                Create account
              </CustomButton>
            </Form>)}
        </Formik>

        <Box className={styles.signInQuestion}>
          <Typography variant='h11' className={styles.signInQuestionText}>Already have an account?</Typography>
          <Link to='/' className={styles.signInLink}>
            <Typography variant='h11Semibold'>Login</Typography>
          </Link>
        </Box>
        <Box className={styles.termsAndConditions}>
          <Typography variant='h11' sx={{textAlign: 'center'}}>By signing up, you understand and agree with TravGenius'</Typography>
          <Typography variant='h11'>
            <Link to='/'>Terms & Conditions</Link> and <Link to='/'>Privacy Policy</Link>
          </Typography>
        </Box>
      </Box>
    </AuthWrapper>
  )
};


export default CreateAccount